/**
 * « Mon abonnement » de Mon compte v2 (SPEC MC1, MC4 ; retour M3 ; retour de Daphné du 07/10).
 *
 * Même choix du montant affiché que la v1 (screens/Account/Subscription/MySubscription.tsx,
 * resolveContractAmount), même prochaine échéance. AUCUN bouton de pause ni de résiliation (M7).
 *
 * Contrats montrés (retour du 07/10 : « la rubrique ABONNEMENT apparaît alors que je n'ai pas
 * d'abonnement actif ») : la v1 montrait tout contrat `AutopayStatus = Active`. Constaté le 07/10 en
 * lecture sur la prod Dynamo Paris : un contrat interne à 0 € (crédits du personnel, renouvelé chaque
 * mois : prélèvements prévus à 0 €) a ce statut, sans être un abonnement payé par le client. Désormais
 * (isActiveSubscription) : statut Active, ni terminé (EndDate passée) ni résilié (TerminationDate
 * atteinte), et payant (au moins un montant du contrat ou de ses prélèvements prévus au-dessus de 0).
 * Aucun : rubrique masquée.
 */
import type { ClientContract, UpcomingAutopayEvent } from 'types/clientContract';
import type { MindbodyContract } from 'types/contract';

export type TagTone = 'green' | 'amber' | 'red' | 'neutral';

/** = MySubscription.tsx statusBadge(), tons de la maquette (SUB_BADGE). */
export function subscriptionStatus(status: string): { label: string; tone: TagTone } {
  switch (status) {
    case 'Active':
      return { label: 'Actif', tone: 'green' };
    case 'Suspended':
    case 'Paused':
      return { label: 'En pause', tone: 'amber' };
    case 'Terminated':
      return { label: 'Résilié', tone: 'red' };
    case 'Inactive':
    default:
      return { label: 'Inactif', tone: 'neutral' };
  }
}

/** « 2026-10-07T00:00:00 » → « 2026-10-07 » (dates Mindbody sans fuseau, heure du site) ; null si absente. */
const dayOf = (value: string | null | undefined): string | null =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null;

/** Jour local « AAAA-MM-JJ ». */
const localDay = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const positive = (value: unknown) => typeof value === 'number' && value > 0;

/**
 * Contrat payant ? Au moins un montant au-dessus de 0 parmi ceux du contrat du client (prélèvement
 * normal, premier, dernier) et ceux de ses prélèvements prévus (ChargeAmount, Amount). Montants tous
 * absents (champ non renvoyé) : on se fie au catalogue (prix récurrent ou premier paiement), comme
 * resolveContractAmount. Un contrat à 0 € (crédits du personnel, offert) n'est pas un abonnement.
 */
export function isPaidContract(contract: ClientContract, template?: MindbodyContract): boolean {
  const own = [contract.NormalAutoPay, contract.FirstAutoPay, contract.LastAutoPay];
  const events = (contract.UpcomingAutopayEvents ?? []).flatMap((e) => [e.ChargeAmount, e.Amount]);
  if ([...own, ...events].some(positive)) return true;
  const known = [...own, ...events].some((value) => typeof value === 'number');
  return !known && (positive(template?.RecurringPaymentAmountTotal) || positive(template?.FirstPaymentAmountTotal));
}

/**
 * Abonnement réellement actif (retour du 07/10) : prélèvements actifs (AutopayStatus « Active » ; en
 * pause, suspendu, inactif ou résilié : non), contrat ni terminé (EndDate avant aujourd'hui) ni résilié
 * (TerminationDate aujourd'hui ou avant), et payant (isPaidContract).
 */
export function isActiveSubscription(contract: ClientContract, template?: MindbodyContract, now: Date = new Date()): boolean {
  if (contract.AutopayStatus !== 'Active') return false;
  const today = localDay(now);
  const end = dayOf(contract.EndDate);
  if (end && end < today) return false;
  const termination = dayOf(contract.TerminationDate);
  if (termination && termination <= today) return false;
  return isPaidContract(contract, template);
}

/** Contrats affichés dans « Mon abonnement » (aucun : rubrique masquée). */
export function activeContracts(
  contracts: ClientContract[] | undefined,
  catalogue?: MindbodyContract[],
  now: Date = new Date(),
): ClientContract[] {
  return (contracts ?? []).filter((c) =>
    isActiveSubscription(
      c,
      catalogue?.find((t) => t.Id === c.ContractID),
      now,
    ),
  );
}

/**
 * Le catalogue (GET sale/contracts) n'est utile que pour un contrat actif dont les montants propres
 * ne disent rien (champ absent) : montant affiché et décision « payant ». Sinon, pas d'appel.
 */
export function needsCatalogue(contracts: ClientContract[] | undefined, now: Date = new Date()): boolean {
  return (contracts ?? []).some(
    // Catalogue fictif « payant » : vrai pour un contrat qui serait montré si le catalogue le dit payant.
    (c) => !positive(c.NormalAutoPay) && isActiveSubscription(c, { RecurringPaymentAmountTotal: 1 } as MindbodyContract, now),
  );
}

/** = MySubscription.tsx countRemainingEvents() */
export function countRemainingEvents(events: UpcomingAutopayEvent[] | null | undefined): number {
  if (!events) return 0;
  return events.length;
}

/** = MySubscription.tsx nextEventDate() */
export function nextEventDate(events: UpcomingAutopayEvent[] | null | undefined): string | null {
  if (!events || events.length === 0) return null;
  const first = events[0];
  return (first.ScheduleDate as string | undefined) ?? null;
}

/**
 * = MySubscription.tsx resolveContractAmount() : prélèvement récurrent du contrat, sinon tarif
 * récurrent du catalogue, sinon premier paiement du catalogue, sinon ce que renvoie Mindbody.
 */
export function resolveContractAmount(
  clientContract: ClientContract,
  template: MindbodyContract | undefined,
): { label: string; amount: number | null } {
  if (clientContract.NormalAutoPay && clientContract.NormalAutoPay > 0) {
    return { label: 'Prélèvement mensuel', amount: clientContract.NormalAutoPay };
  }
  if (template?.RecurringPaymentAmountTotal && template.RecurringPaymentAmountTotal > 0) {
    return { label: 'Prélèvement mensuel', amount: template.RecurringPaymentAmountTotal };
  }
  if (template?.FirstPaymentAmountTotal && template.FirstPaymentAmountTotal > 0) {
    return { label: 'Paiement unique', amount: template.FirstPaymentAmountTotal };
  }
  return { label: 'Prélèvement mensuel', amount: clientContract.NormalAutoPay ?? clientContract.FirstAutoPay ?? null };
}
